import { PLAYSTYLES_PLUS_CAP } from "@/config/game";
import {
  RangeFilterType,
  SelectorFilterType,
  SelectorOption,
  UpdateFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import type { VersionAssets } from "@/redux/loadedContent/loadedContentSlice";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type { PlayersSortByType } from "@repo/shared/api";
import { evolutionPathParam } from "@repo/shared/evolutions";
import { fetchBaseData } from "@repo/shared/redux";
import {
  CardFont,
  Cosmetic,
  Cost,
  EvoPathId,
  EvolutionMetaData,
  Foil,
  MetaRating,
  ObjectiveDisplay,
  PriceInfo,
} from "@repo/shared/types";
import { URLizeString } from "@repo/shared/utils";
import { inGameAttributesDict } from "@repo/shared/utils";

import { APIStatus } from "../../api/APIStatus.type";
import { getPlayers } from "../../api/api";
import { QueryParams } from "../../pages/Players/PlayersPage";
import { bodyTypeIdToName } from "../squadBuilder/squadBuilderUtils";
import { IRootState } from "../store";
import { usedFeature } from "../user/userSlice";

export const defaultPositions = [
  { category: "Attackers", label: "LW", id: 0 },
  { category: "Attackers", label: "ST", id: 1 },
  { category: "Attackers", label: "RW", id: 2 },
  { category: "Midfielders", label: "LM", id: 3 },
  { category: "Midfielders", label: "CAM", id: 4 },
  { category: "Midfielders", label: "RM", id: 5 },
  { category: "Midfielders", label: "CDM", id: 6 },
  { category: "Midfielders", label: "CM", id: 7 },
  { category: "Defenders", label: "LB", id: 8 },
  { category: "Defenders", label: "CB", id: 9 },
  { category: "Defenders", label: "RB", id: 10 },
  { category: "Defenders", label: "GK", id: 11 },
];

type inGameAttributes = {
  [key: string]: number;
};

export type MetaRatingsPlayer = {
  resourceId: number;
  assetId: number;
  eaPlayerId: number | null;
  playerRoleId: number;
  name: string;
  rating: number;
  positions: string[];
  possiblePositions: string[];
  metaRating: number;
  metaRatingDelta?: number;
  metaRatingZeroChem: number;
  chemStyleId?: number;
  metaRatings?: MetaRating[];
  inGameAttributes?: inGameAttributes;
  sbcStorage: boolean;
  newPlayer: boolean;
  owned: boolean;
  attributes: number[];
  countryId: number;
  leagueId: number;
  clubId: number;
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
  skillMoves: number;
  weakFoot: number;
  preferredFoot: "left" | "right";
  untradeable: boolean;
  firstOwner?: boolean;
  versionId: number;
  preferredPosition: string;
  metalId: number;
  hasDynamicImage: boolean;
  playerUrl: string;
  playStylesPlus: number[];
  isSbcPlayer: boolean;
  isObjectivePlayer: boolean;
  cardName: string;
  cardFont?: CardFont;
  foil?: Foil;
  cosmetics?: Cosmetic[];
  rolesPlus: number[];
  evolutionPath?: number[];
  pathwayPath?: number[][];
  playStyles?: number[];
  canEvolve: boolean;
  availableEvos?: number;
  evoPathId?: EvoPathId;
  evoPopularity?: number;
  evolutionData?: EvolutionMetaData;
  itemScore?: number;
};

export type HeatmapCell = {
  x: number;
  y: number;
  value: number;
};

export type Focus = {
  name: string;
  description: string;
  advantages: string[];
  heatmaps: {
    [key: string]: HeatmapCell[];
  };
  neutral: string[];
  disadvantages: string[];
  positions: string[];
};

export type PlayerRole = {
  id: number;
  name: string;
  eligiblePositions: string[];
  description: string;
  position: string;
  metaTiers: [
    {
      tierId: 0 | 1 | 2 | 3 | 4;
      minMetaRating: number;
    },
  ];
  index: number;
  focuses: Focus[];
  shortName?: string;
  topStats: string[];
  topAccelerates: string[];
  topPlayStylesPlus: number[];
  squadMetaRatingWeight?: number;
};

export type Tier = {
  tierName: "S" | "A+" | "A" | "B+" | "B";
  tierColor?: string;
  tierBorderColor?: string;
  tierTextColor: string;
  splitTierName?: string[];
};

export const getTier = (
  playerRoles: PlayerRole[],
  metaRating: number,
  playerRoleId: number,
  newDesign: boolean = false,
) => {
  const playerRole = playerRoles.find((a) => a.id === playerRoleId);
  if (!playerRole) return null;

  const roundedMetaRating = Math.round(metaRating * 10) / 10;

  const tier = playerRole.metaTiers.find(
    (t) => roundedMetaRating >= t.minMetaRating,
  );
  // Below every band = no tier at all; the chip is hidden rather than shown
  // as a placeholder.
  if (!tier) return;

  return tierIdToTier(tier.tierId, newDesign);
};

// Stable Tier objects to avoid returning a new reference each time
const TIER_MAP: Record<0 | 1 | 2 | 3 | 4, Tier> = {
  0: {
    tierName: "S",
    tierColor: "bg-primary-light-blue",
    tierTextColor: "text-primary-light-blue",
    splitTierName: ["S"],
  },
  1: {
    tierName: "A+",
    tierColor: "bg-ea-dark-green",
    tierTextColor: "text-ea-dark-green",
    splitTierName: ["A", "+"],
  },
  2: {
    tierName: "A",
    tierColor: "bg-ea-light-green",
    tierTextColor: "text-ea-light-green",
    splitTierName: ["A"],
  },
  3: {
    tierName: "B+",
    tierColor: "bg-tier-yellow",
    tierTextColor: "text-tier-yellow",
    splitTierName: ["B", "+"],
  },
  4: {
    tierName: "B",
    tierColor: "bg-ea-orange",
    tierTextColor: "text-ea-orange",
    splitTierName: ["B"],
  },
};

const NEW_DESIGN_TIER_MAP: Record<0 | 1 | 2 | 3 | 4, Tier> = {
  0: {
    tierName: "S",
    tierColor: "bg-s-tier-background",
    tierTextColor: "text-s-tier-foreground",
    tierBorderColor: "border-s-tier-border",
    splitTierName: ["S"],
  },
  1: {
    tierName: "A+",
    tierColor: "bg-a-plus-tier-background",
    tierTextColor: "text-a-plus-tier-foreground",
    tierBorderColor: "border-a-plus-tier-border",
    splitTierName: ["A", "+"],
  },
  2: {
    tierName: "A",
    tierColor: "bg-a-tier-background",
    tierTextColor: "text-a-tier-foreground",
    tierBorderColor: "border-a-tier-border",
    splitTierName: ["A"],
  },
  3: {
    tierName: "B+",
    tierColor: "bg-b-plus-tier-background",
    tierTextColor: "text-b-plus-tier-foreground",
    tierBorderColor: "border-b-plus-tier-border",
    splitTierName: ["B", "+"],
  },
  4: {
    tierName: "B",
    tierColor: "bg-b-tier-background",
    tierTextColor: "text-b-tier-foreground",
    tierBorderColor: "border-b-tier-border",
    splitTierName: ["B"],
  },
};

export const tierIdToTier = (
  tierId: 0 | 1 | 2 | 3 | 4,
  newDesign: boolean = false,
): Tier => (newDesign ? NEW_DESIGN_TIER_MAP[tierId] : TIER_MAP[tierId]);

const TIER_NAME_TO_ID: Record<Tier["tierName"], 0 | 1 | 2 | 3 | 4> = {
  S: 0,
  "A+": 1,
  A: 2,
  "B+": 3,
  B: 4,
};

/** Resolve a backend tier name ("S", "A+", …) to its Tier design tokens. */
export const tierNameToTier = (
  tierName: string,
  newDesign: boolean = false,
): Tier | undefined => {
  const tierId = TIER_NAME_TO_ID[tierName as Tier["tierName"]];
  return tierId === undefined ? undefined : tierIdToTier(tierId, newDesign);
};

type PlayStyle = {
  name: string;
  /** Localized category label; `category` stays the English grouping key. */
  categoryName?: string;
  category:
    | "Scoring"
    | "Finishing"
    | "Passing"
    | "Defending"
    | "Ball Control"
    | "Physical"
    | "Goalkeeper";
  id: number;
};

export type PlayersFrom = "market" | "both" | "club";

export type PlayStylesDict = {
  [id: number]: PlayStyle;
};

export type SelectedPlayStyle = {
  id: number;
  plus: boolean;
};

type RequestedFromPage =
  | "players"
  | "evolutions-builder"
  | "evolutions"
  | "playstyles-lab";

/*
  Sent to the backend as a bare `favorite-clubs-and-countries` flag, which
  OR-matches the signed-in user's saved clubs and country (IDs come from the user
  record, not the query). It lives in `playerFilters` so it round-trips through
  the URL and renders a clearable chip, but it carries no value of its own and
  must be lifted out of the query params before the request.
*/
export const FAVORITE_CLUBS_AND_COUNTRIES_KEY = "favoriteClubsAndCountries";

export type PlayersState = {
  players: MetaRatingsPlayer[];
  evolutionPlayers: MetaRatingsPlayer[];
  totalPages: number;
  playerRoles: PlayerRole[];
  playStylesDict: PlayStylesDict;
  playersStatus: APIStatus;
  initialPlayerDataStatus: APIStatus;
  requestNo: number;
  playerFilters: (RangeFilterType | SelectorFilterType)[];
  requestedFromPage?: RequestedFromPage;
};

const initialState: PlayersState = {
  players: [],
  evolutionPlayers: [],
  totalPages: 1,
  playerRoles: [],
  playStylesDict: {},
  playersStatus: "idle",
  initialPlayerDataStatus: "idle",
  requestNo: 0,
  // Labels here are ids (API values, URL params); display text is looked up
  // per key in organisms/Filters/PlayerFilters/filterLabels.ts.
  playerFilters: [
    {
      label: "Gender",
      key: "gender",
      type: "SELECTOR_FILTER",
      useLabelAsId: true,
      options: [
        { id: 0, label: "Male", category: "Gender" },
        { id: 1, label: "Female", category: "Gender" },
      ],
      selectedOptions: [],
    },
    {
      label: "Owned Players",
      key: "ownedOrEvolved",
      type: "SELECTOR_FILTER",
      options: [
        { id: 0, label: "My Club", category: "Owned" },
        { id: 1, label: "My Evolutions", category: "Evolved" },
        { id: 2, label: "Squad", category: "ActiveSquad" },
      ],
      selectedOptions: [],
    },

    {
      label: "Price",
      key: "price",
      type: "RANGE_FILTER",
      range: [0, Number.MAX_VALUE],
      minValue: null,
      maxValue: null,
    },
    {
      label: "Rating",
      key: "rating",
      type: "RANGE_FILTER",
      range: [0, 99],
      minValue: null,
      maxValue: null,
    },
    {
      label: "Meta Rating",
      key: "metaRating",
      type: "RANGE_FILTER",
      range: [0, 99],
      minValue: null,
      maxValue: null,
    },
    {
      label: "Past and Present Clubs",
      key: "pastAndPresentClubs",
      shortLabel: "Past and Present Clubs",
      type: "SELECTOR_FILTER",
      options: [
        {
          id: 1,
          label: "Past and Present",
          category: "Past and Present",
        },
      ],
      selectedOptions: [],
    },
    {
      label: "Favorite Clubs & Countries",
      key: FAVORITE_CLUBS_AND_COUNTRIES_KEY,
      type: "SELECTOR_FILTER",
      options: [{ id: 1, label: "My Favorites", category: "Favorites" }],
      selectedOptions: [],
    },
    {
      label: "Country",
      key: "country",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "Club",
      key: "club",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "League",
      key: "league",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "Position",
      key: "position",
      type: "SELECTOR_FILTER",
      useLabelAsId: true,
      options: [...defaultPositions].map((position, i) => ({
        id: i,
        label: position.label,
        category: position.category,
      })),
      selectedOptions: [],
    },
    {
      label: "Rarity",
      key: "rarity",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "PlayStyles",
      key: "playStyles",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "PlayStyles+",
      key: "playStylesPlus",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "Body Type",
      key: "body-types",
      type: "SELECTOR_FILTER",
      options: Object.entries(bodyTypeIdToName)
        .slice(0, 11)
        .map(([id, name]) => ({
          id: parseInt(id),
          label: name[0],
          category: "Body Type",
        })),
      selectedOptions: [],
    } as SelectorFilterType,
    {
      label: "AcceleRATEs",
      key: "accelerates",
      type: "SELECTOR_FILTER",
      useLabelAsId: true,
      options: [
        {
          id: 0,
          label: "Explosive",
          category: "Accelerates",
        },
        {
          id: 1,
          label: "Controlled",
          category: "Accelerates",
        },
        {
          id: 2,
          label: "Lengthy",
          category: "Accelerates",
        },
      ],
      selectedOptions: [],
    },
    {
      label: "Skill Moves",
      shortLabel: "SM",
      key: "skillMoves",
      type: "SELECTOR_FILTER",
      options: [
        { id: 1, label: "1", category: "Skill Moves" },
        { id: 2, label: "2", category: "Skill Moves" },
        { id: 3, label: "3", category: "Skill Moves" },
        { id: 4, label: "4", category: "Skill Moves" },
        { id: 5, label: "5", category: "Skill Moves" },
      ],
      selectedOptions: [],
    },
    {
      label: "Weak Foot",
      shortLabel: "WF",
      key: "weakFoot",
      type: "SELECTOR_FILTER",
      options: [
        { id: 1, label: "1", category: "Weak Foot" },
        { id: 2, label: "2", category: "Weak Foot" },
        { id: 3, label: "3", category: "Weak Foot" },
        { id: 4, label: "4", category: "Weak Foot" },
        { id: 5, label: "5", category: "Weak Foot" },
      ],
      selectedOptions: [],
    },
    {
      label: "Height",
      key: "height",
      type: "RANGE_FILTER",
      range: [100, 220],
      minValue: null,
      maxValue: null,
    } as RangeFilterType,
    {
      label: "Off-Balance Dribbling",
      key: "offBalanceDribbling",
      type: "RANGE_FILTER",
      range: [75, 99],
      minValue: null,
      maxValue: null,
    },
    {
      label: "Preferred Foot",
      shortLabel: "PF",
      key: "preferredFoot",
      type: "SELECTOR_FILTER",
      useLabelAsId: true,
      options: [
        { id: 0, label: "Left", category: "Preferred Foot" },
        { id: 1, label: "Right", category: "Preferred Foot" },
      ],
      selectedOptions: [],
    },
    {
      label: "Max PlayStyles",
      key: "maxPlayStyles",
      type: "RANGE_FILTER",
      range: [0, 10],
      minValue: null,
      maxValue: null,
    } as RangeFilterType,
    {
      label: "Max PlayStyles+",
      key: "maxPlayStylesPlus",
      type: "RANGE_FILTER",
      range: [0, PLAYSTYLES_PLUS_CAP],
      minValue: null,
      maxValue: null,
    } as RangeFilterType,
    ...Object.entries(inGameAttributesDict).map(
      ([key, attribute]) =>
        ({
          label: attribute.label,
          key,
          type: "RANGE_FILTER",
          range: [1, 99],
          minValue: null,
          maxValue: null,
        }) as RangeFilterType,
    ),
    {
      label: "Max Chain Length",
      key: "max-chain-length",
      type: "RANGE_FILTER",
      range: [1, 19],
      minValue: null,
      maxValue: null,
    } as RangeFilterType,
    {
      label: "Max Number of Games",
      key: "max-games",
      type: "RANGE_FILTER",
      range: [1, 29],
      minValue: null,
      maxValue: null,
    } as RangeFilterType,
  ],
};

export const fetchPlayers = createAsyncThunk(
  "fetch-players",
  async (
    {
      page,
      sort,
      myClub,
      myEvolvedPlayers,
      activeSquad,
      chainedEvo,
      excludePaidEvo,
      excludeCompletedEvolutions,
      playerRoleId,
      playerRoleIds,
      queryParams,
      evolutions,
      evoIds,
      mustIncludeEvoIds,
      searchText,
      requestedFromPage,
      playStylesLab,
    }: {
      page: number;
      sort: PlayersSortByType;
      myClub?: boolean;
      myEvolvedPlayers?: boolean;
      activeSquad?: boolean;
      chainedEvo?: boolean;
      excludePaidEvo?: boolean;
      excludeCompletedEvolutions?: boolean;
      playerRoleId?: number;
      playerRoleIds?: number[];
      queryParams?: QueryParams[];
      evolutions?: boolean;
      evoIds?: number[];
      mustIncludeEvoIds?: number[];
      requestNo?: number;
      searchText?: string;
      requestedFromPage: RequestedFromPage;
      playStylesLab?: boolean;
    },
    { dispatch, getState },
  ) => {
    const { playersReducer } = getState() as IRootState;
    let { requestNo } = playersReducer;
    requestNo += 1;
    dispatch(incrementRequestNo(requestNo));
    const activeFilters = activeFiltersSelector(getState() as IRootState);

    const favoriteClubsAndCountries = queryParams?.some(
      (qp) => FAVORITE_CLUBS_AND_COUNTRIES_KEY in qp,
    );

    const res = await getPlayers({
      page,
      sort,
      myClub,
      myEvolvedPlayers,
      activeSquad,
      chainedEvo,
      excludePaidEvo,
      excludeCompletedEvolutions,
      playerRoleId,
      playerRoleIds,
      queryParams: queryParams?.filter(
        (qp) => !(FAVORITE_CLUBS_AND_COUNTRIES_KEY in qp),
      ),
      evolutions,
      evoIds,
      mustIncludeEvoIds,
      searchText,
      playStylesLab,
      favoriteClubsAndCountries,
    });

    if (evolutions && activeFilters.length > 0) {
      dispatch(usedFeature({ feature: "VIEW_EVO_PLAYER_WITH_FILTER" }));
    } else if (!evolutions && activeFilters.length > 0) {
      dispatch(usedFeature({ feature: "VIEW_PLAYER_WITH_FILTER" }));
    }
    return {
      ...res,
      requestNo,
      isEvolutionPlayers: evolutions,
      requestedFromPage,
    };
  },
);

export const getRolesPlusOrPlusPlus = ({
  playerRoleId,
  rolesPlus,
}: {
  playerRoleId: number;
  rolesPlus: number[];
}): "plus" | "plusPlus" | null => {
  if (rolesPlus?.includes(playerRoleId + 100)) return "plusPlus";
  if (rolesPlus?.includes(playerRoleId)) return "plus";
  return null;
};

export const matchRoleIdFromPlusPlus = ({
  playerRoleId,
  playerRoleIdFromPlusPlus,
}: {
  playerRoleId: number;
  playerRoleIdFromPlusPlus: number;
}) =>
  playerRoleId === playerRoleIdFromPlusPlus ||
  playerRoleId === playerRoleIdFromPlusPlus - 100;

export const getRoleFromPlusPlus = ({
  playerRoleIdFromPlusPlus,
  playerRoles,
}: {
  playerRoleIdFromPlusPlus: number;
  playerRoles: PlayerRole[];
}) => {
  return playerRoles.find((role) =>
    matchRoleIdFromPlusPlus({
      playerRoleId: role.id,
      playerRoleIdFromPlusPlus,
    }),
  );
};

export type BasePath =
  | "/my-club"
  | "/evolutions/players"
  | "/evolutions/builder"
  | "/evolutions/builder/details"
  | "/evolutions/saved"
  | "/playstyles-lab"
  | "/playstyles-lab/details"
  | "/players";

/*
  Player URLs are English regardless of content language (DEV-3626). The
  version segment comes from the backend `slug` on version-assets, since the
  version `name` is localized. The player-name segment is still URLized here:
  player names are not localized, and `name` is `commonName ?? "first last"`,
  the same input the scraper slugs into `playernames.slug`, so the two agree.
*/
export const versionUrlSlug = (version?: { slug?: string; name: string }) =>
  version ? version.slug || URLizeString(version.name) : "";

export const versionNameBySlug = (
  versionAssets: VersionAssets,
  slug?: string,
) =>
  slug
    ? Object.values(versionAssets).find((v) => v.slug === slug)?.name
    : undefined;

export const generateNavigationUrl = ({
  playerRoleId,
  resourceId,
  name,
  versionSlug,
  evolutionPath,
  pathwayPath,
  evoPathId,
  basePath,
  labPlayStylesPlus,
  labPlayStyles,
  chemistry, // TDOO: Add these for squad builder
  eaPlayerId, // TODO: Add these for squad builder
  chemstyleId, // TODO: Add these for squad builder
}: {
  playerRoleId?: number;
  resourceId: number;
  name: string;
  basePath: BasePath;
  versionSlug?: string;
  evolutionPath?: number[];
  pathwayPath?: number[][];
  evoPathId?: EvoPathId;
  canEvolve?: boolean;
  myClub?: boolean;
  chemistry?: number;
  eaPlayerId?: number;
  chemstyleId?: number;
  labPlayStylesPlus?: number[];
  labPlayStyles?: number[];
}) => {
  const queryParams = new URLSearchParams();

  if (playerRoleId != null) {
    queryParams.set("player-role-id", playerRoleId.toString());
  }

  if (evolutionPath?.length > 0) {
    queryParams.set(
      "evolution-path",
      evolutionPathParam(evolutionPath, pathwayPath),
    );
  }
  if (evoPathId) {
    queryParams.set("evo-path-id", evoPathId.toString());
  }

  if (labPlayStylesPlus?.length > 0) {
    queryParams.set("lab-playstyles-plus", labPlayStylesPlus.join(","));
  }
  if (labPlayStyles?.length > 0) {
    queryParams.set("lab-playstyles", labPlayStyles.join(","));
  }

  const queryString = queryParams.toString();
  return `${basePath}/${URLizeString(name)}${versionSlug ? `/${versionSlug}` : ""}/${resourceId}${queryString ? `?${queryString}` : ""}`;
};

type PlayerFilters = (RangeFilterType | SelectorFilterType)[];

/* The filter edits as plain mutations on a filters array. */
export const applyFilterUpdate = (
  filters: PlayerFilters,
  update: UpdateFilterType,
) => {
  const filter = filters.find((f) => f.key === update.key);
  if (filter?.type === "RANGE_FILTER") {
    const { min, max } = update;
    if (min !== undefined) filter.minValue = min;
    if (max !== undefined) filter.maxValue = max;
  } else if (filter?.type === "SELECTOR_FILTER") {
    const { value } = update;
    const isSelected = filter.selectedOptions.includes(value);
    if (
      filter.key === "ownedOrEvolved" ||
      filter.key === "gender" ||
      filter.key === "accelerates"
    ) {
      filter.selectedOptions = isSelected ? [] : [value];
    } else if (isSelected) {
      filter.selectedOptions = filter.selectedOptions.filter(
        (so) => so !== value,
      );
    } else filter.selectedOptions.push(value);
  }
};

export const clearFilterIn = (filters: PlayerFilters, key: string) => {
  const filter = filters.find((f) => f.key === key);
  if (filter?.type === "RANGE_FILTER") {
    filter.minValue = null;
    filter.maxValue = null;
  } else if (filter?.type === "SELECTOR_FILTER") {
    filter.selectedOptions = [];
  }
};

export const clearFilterOptionIn = (
  filters: PlayerFilters,
  key: string,
  option: number,
) => {
  const filter = filters.find((f) => f.key === key);
  if (filter?.type === "SELECTOR_FILTER") {
    filter.selectedOptions = filter.selectedOptions.filter(
      (so) => so !== option,
    );
  }
};

export const clearAllFiltersIn = (filters: PlayerFilters) =>
  filters.forEach((f) => clearFilterIn(filters, f.key));

export const activeFiltersOf = (filters: PlayerFilters) =>
  filters.filter((filter) =>
    filter.type === "RANGE_FILTER"
      ? filter.minValue !== null || filter.maxValue !== null
      : filter.selectedOptions.length > 0,
  );

export const filtersToQueryParams = (filters: PlayerFilters) => {
  const queryParams = [];
  filters.forEach((filter) => {
    if (filter.type === "RANGE_FILTER") {
      if (filter.key === "max-chain-length" || filter.key === "max-games") {
        if (filter.maxValue !== null) {
          queryParams.push({ [`${filter.key}`]: [filter.maxValue] });
        }
        return;
      }
      if (filter.minValue !== null)
        queryParams.push({ [`${filter.key}Min`]: [filter.minValue] });
      if (filter.maxValue !== null)
        queryParams.push({ [`${filter.key}Max`]: [filter.maxValue] });
    } else if (
      filter.type === "SELECTOR_FILTER" &&
      filter.selectedOptions.length > 0
    ) {
      queryParams.push({
        [filter.key]: filter.useLabelAsId
          ? filter.selectedOptions.map(
              (sp) => filter.options.find((p) => p.id === sp).label,
            )
          : filter.selectedOptions,
      });
    }
  });
  return queryParams;
};

const playersSlice = createSlice({
  name: "players",
  initialState,
  reducers: {
    incrementRequestNo: (state, action: PayloadAction<number>) => {
      state.requestNo = action.payload;
    },
    updateFilterOptions: (
      state,
      action: PayloadAction<{ key: string; options: SelectorOption[] }>,
    ) => {
      const filter = state.playerFilters.find(
        (f) => f.key === action.payload.key,
      );
      if (filter?.type === "SELECTOR_FILTER") {
        filter.options = action.payload.options;
      }
    },
    updateFilter: (state, action: PayloadAction<UpdateFilterType>) => {
      applyFilterUpdate(state.playerFilters, action.payload);
    },
    setFilterValues: (
      state,
      action: PayloadAction<{ key: string; values: number[] }>,
    ) => {
      const { key, values } = action.payload;
      const filter = state.playerFilters.find((f) => f.key === key);

      if (filter?.type === "SELECTOR_FILTER") {
        filter.selectedOptions = values;
      }
    },
    clearAllFilters: (state) => {
      clearAllFiltersIn(state.playerFilters);
    },
    clearPlayers: (state) => {
      state.players = [];
      state.totalPages = 1;
      state.playersStatus = "idle";
    },
    clearFilter: (state, action: PayloadAction<string>) => {
      clearFilterIn(state.playerFilters, action.payload);
    },
    clearFilterOption: (
      state,
      action: PayloadAction<{ filterKey: string; option: number }>,
    ) => {
      clearFilterOptionIn(
        state.playerFilters,
        action.payload.filterKey,
        action.payload.option,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayers.fulfilled, (state, action) => {
        if (action.payload.requestNo !== state.requestNo) {
          return;
        }
        if (action.payload.isEvolutionPlayers) {
          state.evolutionPlayers = action.payload.players;
        } else {
          state.players = action.payload.players;
        }
        state.totalPages = action.payload.totalPages;
        state.playersStatus = "fulfilled";
        state.requestedFromPage = action.payload.requestedFromPage;
      })
      .addCase(fetchPlayers.pending, (state) => {
        state.playersStatus = "pending";
      })
      .addCase(fetchPlayers.rejected, (state) => {
        state.playersStatus = "rejected";
      })
      .addCase(fetchBaseData.fulfilled, (state, action) => {
        const { playStyles, playerRoles, versionAssets, managerData } =
          action.payload;
        state.playStylesDict = playStyles;
        state.playerRoles = playerRoles;

        const setOptions = (key: string, options: SelectorOption[]) => {
          const filter = state.playerFilters.find((f) => f.key === key);
          if (filter?.type === "SELECTOR_FILTER") filter.options = options;
        };

        const playStyleOptions = Object.entries(playStyles).map(
          ([id, playStyle]) => ({
            id: parseInt(id),
            label: playStyle.name,
            value: id,
            category: playStyle.category,
          }),
        );
        setOptions("playStyles", playStyleOptions);
        setOptions("playStylesPlus", playStyleOptions);

        setOptions(
          "rarity",
          Object.entries(versionAssets)
            .filter(([_, rarity]) => !rarity.hidden)
            .map(([key, rarity]) => ({
              id: parseInt(key),
              value: key,
              label: rarity.name,
              category: "Rarity" as const,
              createdAt: rarity.createdAt,
            }))
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            ),
        );
        setOptions(
          "country",
          managerData.countries.map((country) => ({
            id: country.id,
            value: country.id,
            label: country.name,
            category: "Country",
          })),
        );
        setOptions(
          "league",
          managerData.leagues.map((league) => ({
            id: league.id,
            value: league.id,
            label: league.name + (league.isWomen ? " (w)" : ""),
            category: "League",
          })),
        );
        setOptions(
          "club",
          managerData.clubs.map((club) => ({
            id: club.id,
            value: club.id,
            label: club.name + (club.isWomen ? " (w)" : ""),
            category: "Club",
          })),
        );
        state.initialPlayerDataStatus = "fulfilled";
      })
      .addCase(fetchBaseData.pending, (state) => {
        state.initialPlayerDataStatus = "pending";
      })
      .addCase(fetchBaseData.rejected, (state) => {
        state.initialPlayerDataStatus = "rejected";
      });
  },
});

export const {
  incrementRequestNo,
  updateFilter,
  updateFilterOptions,
  setFilterValues,
  clearAllFilters,
  clearFilter,
  clearFilterOption,
  clearPlayers,
} = playersSlice.actions;

export const playersSelector = (state: IRootState) => state.playersReducer;

export const activeFiltersSelector = (state: IRootState) =>
  activeFiltersOf(state.playersReducer.playerFilters);

export const ownedOrEvolvedSelector = (state: IRootState) => {
  const { playerFilters } = state.playersReducer;
  const filter = playerFilters.find(
    (f) => f.key === "ownedOrEvolved",
  ) as SelectorFilterType;
  const selectedOption = filter.options.find(
    (o) => o.id === filter?.selectedOptions[0],
  );
  return selectedOption;
};

// Memoized: builds a new array, so an unmemoized version re-renders its page
// on every dispatched action.
export const playerFiltersAsQueryParamsSelector = createSelector(
  [(state: IRootState) => state.playersReducer.playerFilters],
  filtersToQueryParams,
);

export const tierSelector =
  (
    metaRating: number | null,
    playerRoleId: number | null,
    newDesign: boolean = false,
  ) =>
  (state: IRootState) => {
    if (!metaRating || !playerRoleId) return null;
    const { playerRoles } = state.playersReducer;
    return getTier(playerRoles, metaRating, playerRoleId, newDesign);
  };

export const playerRoleLabelSelector =
  ({
    playerRoleId,
    appendPosition = true,
    shortName = false,
  }: {
    playerRoleId: number;
    appendPosition?: boolean;
    shortName?: boolean;
  }) =>
  ({ playersReducer, loadedContentReducer }: IRootState) => {
    const playerRole = playersReducer.playerRoles.find(
      (pr) => pr.id === playerRoleId,
    );
    if (!playerRole) return "";
    if (!appendPosition) return playerRole.name;
    const position =
      loadedContentReducer.labels.positions[playerRole.position]?.name ??
      playerRole.position;
    if (shortName) return playerRole.shortName + " " + position;
    return playerRole?.name ? playerRole?.name + " " + position : "";
  };

export const getPlayerRoleByIdSelector =
  ({ playerRoleId }: { playerRoleId: number }) =>
  ({ playersReducer }: IRootState) => {
    return playersReducer.playerRoles.find((pr) => pr.id === playerRoleId);
  };

export const playerRoleByPositionSelector =
  ({ position }: { position: string }) =>
  ({ playersReducer }: IRootState) => {
    return playersReducer.playerRoles.filter(
      (playerRole) => playerRole.position === position,
    );
  };

/** English playstyle category key -> localized label from /players/play-styles. */
export const playStyleCategoryNamesSelector = createSelector(
  (state: IRootState) => state.playersReducer.playStylesDict,
  (dict) =>
    Object.values(dict).reduce<Record<string, string>>((acc, p) => {
      if (p.categoryName) acc[p.category] = p.categoryName;
      return acc;
    }, {}),
);

export const playStylesSelector = (state: IRootState) => {
  return Object.entries(state.playersReducer.playStylesDict).map(
    ([id, playStyle]) => {
      return {
        id: parseInt(id),
        label: playStyle.name,
        value: id,
        category: playStyle.category,
      };
    },
  );
};

export type BasePlayerTierAndMetaRating = {
  metaRating: number;
  tier?: Tier;
};

export const playerRolesSelector = (state: IRootState) =>
  state.playersReducer.playerRoles;

export default playersSlice.reducer;
